import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import VehiclesSearch from './search/VehiclesSearch';
import VehiclesTable from './table/VehiclesTable';
import { setQueryString, useQueryString } from '../common/helpers/queryString';
import { searchAuctions } from './VehicleActions';
import FilterButtons from '../common/components/FilterButtons';
import PageBreadcrumb from '../common/components/layout/PageBreadcrumb';
import { AuctionStatusNameNoDraft } from '../constants/AuctionStatus';
import RouterPaths from '../constants/RouterPaths';
import { PrepareUrl } from '../common/helpers/PrepareUrl';
import { getDealer } from '../dealers/DealersActions';

let breadcrumbs = [];

function VehiclesPage() {
  const { dealerId } = useParams();
  const query = useQueryString();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const previousSearchParams = useSelector((state) => state.vehicles.get('searchParams'));
  const items = useSelector((state) => state.vehicles.get('items'));
  const totalItems = useSelector((state) => state.vehicles.get('totalItems'));
  const dealerDetails = useSelector((state) => state.dealers.get('dealerDetails'));

  const [dealerName, setDealerName] = useState(null);

  const initialSearchParams = {
    offset: query.get('page') ? +query.get('page') - 1 : 0,
    status: Object.keys(AuctionStatusNameNoDraft()),
    sellerOrganizationId: dealerId || null,
    searchCriteria: {},
  };

  const [searchParams, setSearchParams] = useState(
    previousSearchParams?.status ? previousSearchParams : initialSearchParams,
  );

  useEffect(() => {
    if (dealerId) {
      dispatch(getDealer(dealerId));
    } else {
      setDealerName(null);
    }
  }, [dispatch, dealerId]);

  useEffect(() => {
    if (dealerId && dealerDetails?.organization) {
      setDealerName(dealerDetails.organization.officialCompanyName || '');
    }
  }, [dealerDetails, dealerId]);

  useEffect(() => {
    if (dealerId) {
      setSearchParams((prevState) => ({
        ...prevState,
        sellerOrganizationId: dealerId || null,
      }));
    }
  }, [dealerId]);

  useEffect(() => {
    const flattenedSearchParams = {
      ...searchParams,
      ...searchParams.searchCriteria,
    };
    delete flattenedSearchParams.searchCriteria;
    dispatch(searchAuctions(flattenedSearchParams));
  }, [dispatch, searchParams]);

  const handleSearch = (values, setSubmitting) => {
    setSearchParams((prevState) => ({
      ...prevState,
      searchCriteria: values,
      offset: 0,
    }));
    setSubmitting(false);
  };

  const handleChangeStatus = (status) => {
    if (status === '') {
      status = Object.keys(AuctionStatusNameNoDraft());
    } else {
      status = [status];
    }
    setSearchParams((prevState) => ({ ...prevState, status, offset: 0 }));
  };

  const changeOffset = (value) => {
    setSearchParams((prevState) => ({ ...prevState, offset: value }));
    navigate(setQueryString(location, 'page', value + 1));
  };

  if (dealerId) {
    breadcrumbs = [
      { id: 1, type: 'home' },
      { id: 2, type: 'link', text: 'Dealers', url: RouterPaths.DEALERS },
      {
        id: 3,
        type: 'link',
        text: 'Edit Dealer',
        url: PrepareUrl(RouterPaths.DEALER_INFORMATION, { id: dealerId }),
      },
      { id: 4, type: 'span', text: 'Vehicles' },
    ];
  } else {
    breadcrumbs = [
      { id: 1, type: 'home' },
      { id: 2, type: 'span', text: 'Vehicles' },
    ];
  }

  return (
    <Container fluid>
      <PageBreadcrumb items={breadcrumbs} pageTitle="Vehicles" />
      <VehiclesSearch onSearch={handleSearch} dealerName={dealerName} />
      <FilterButtons
        onStatusChange={handleChangeStatus}
        data={AuctionStatusNameNoDraft()}
        previousSelectedStatus={
          searchParams?.status?.length === 1 ? searchParams?.status[0] : null
        }
      />
      <VehiclesTable
        searchParams={searchParams}
        setOffset={changeOffset}
        offset={searchParams.offset}
        data={{ items, total: totalItems }}
      />
    </Container>
  );
}

export default VehiclesPage;
